import { nanoid } from 'nanoid';
import tinycolor from "tinycolor2";

export default function (element, data) {
    const width = data.width;
    const height = data.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = data.radius || Math.min(width, height) * 0.4;
    const offset = data.offset || 0;
    const hoverOffset = data.hoverOffset || 15;
    const depth = data.depth || 15;
    
    const chartData = data.chart.map(function (row) { row.value = parseFloat(row.value); return row; }); 

    element.style.width = width + "px";
    element.style.height = height + "px";

    const total = chartData.reduce(function (result, row) {
        return result + row.value;
    }, 0);

    let position = -Math.PI / 2;
    for (const chartDataRow of chartData) {
        chartDataRow.id = "pie-chart-group-" + nanoid();
        chartDataRow.sectorBegin = position;
        position += (chartDataRow.value / total) * Math.PI * 2;
        chartDataRow.sectorEnd = position;
    }

    let svgOut = "";
    let htmlOut = "";

    htmlOut += "<style>";
    for (const chartDataRow of chartData) {
        const offsetDir = (chartDataRow.sectorBegin + chartDataRow.sectorEnd) / 2;
        
        const hoverOffsetX = Math.cos(offsetDir) * hoverOffset;
        const hoverOffsetY = Math.sin(offsetDir) * hoverOffset;

        const tooltipOffsetX = Math.cos(offsetDir) * (radius * 0.7) + centerX;
        const tooltipOffsetY = Math.sin(offsetDir) * (radius * 0.7) + centerY;

        htmlOut += `.pie-chart-drawing.${chartDataRow.id}.pie-chart-hover { transform: translate(${hoverOffsetX}px, ${hoverOffsetY}px); } `;
        htmlOut += `.${chartDataRow.id}-tooltip { left: ${tooltipOffsetX}px; top: ${tooltipOffsetY}px; } `;
    }
    htmlOut += "</style>";

    for (const chartDataRow of chartData) {
        svgOut = generateSide1(chartDataRow.id, chartDataRow.sectorBegin, chartDataRow.sectorEnd, chartDataRow.color) + svgOut;
        svgOut += generateSide2(chartDataRow.id, chartDataRow.sectorBegin, chartDataRow.sectorEnd, chartDataRow.color);
    }
    
    for (const chartDataRow of chartData) {
        svgOut += generateShadow(chartDataRow.id, chartDataRow.sectorBegin, chartDataRow.sectorEnd, chartDataRow.color);
    }

    for (const chartDataRow of chartData) {
        svgOut += generateFront(chartDataRow.id, chartDataRow.sectorBegin, chartDataRow.sectorEnd, chartDataRow.color);
    }

    htmlOut += `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${svgOut}</svg>`;
    
    for (const chartDataRow of chartData) {
        htmlOut += `<div class="pie-chart-tooltip ${chartDataRow.id} ${chartDataRow.id}-tooltip">${chartDataRow.tooltip}</div>`;
    }

    element.innerHTML = htmlOut;

    element.querySelectorAll(".pie-chart-handle-hover").forEach(function (path) {
        path.addEventListener("mouseover", function (event) {
            const groupSelector = event.target.dataset.hoverGroup;
            element.querySelectorAll(groupSelector).forEach(function (el) {
                el.classList.add("pie-chart-hover");
            });
        });

        path.addEventListener("mouseout", function (event) {
            const groupSelector = event.target.dataset.hoverGroup;
            element.querySelectorAll(groupSelector).forEach(function (el) {
                el.classList.remove("pie-chart-hover");
            });
        });
    });

    function generateFront(id, sectorBegin, sectorEnd, color) {
        const offsetDir = (sectorBegin + sectorEnd) / 2;
        const offsetX = Math.cos(offsetDir) * offset;
        const offsetY = Math.sin(offsetDir) * offset;

        const point0X = centerX + offsetX;
        const point0Y = centerY + offsetY;

        if (sectorEnd - sectorBegin < Math.PI) {
            const point1X = Math.cos(sectorBegin) * radius + centerX + offsetX;
            const point1Y = Math.sin(sectorBegin) * radius + centerY + offsetY;

            const point2X = Math.cos(sectorEnd) * radius + centerX + offsetX;
            const point2Y = Math.sin(sectorEnd) * radius + centerY + offsetY;

            return `<path class="pie-chart-drawing ${id} pie-chart-handle-hover" data-hover-group=".${id}" d="M ${point0X} ${point0Y} L ${point1X} ${point1Y} A ${radius} ${radius} 0 0 1 ${point2X} ${point2Y} Z" fill="${color}" stroke="${color}" stroke-width="0.5"/>`;
        } else {
            const sectorMiddle = (sectorBegin + sectorEnd) / 2;

            const point1X = Math.cos(sectorBegin) * radius + centerX + offsetX;
            const point1Y = Math.sin(sectorBegin) * radius + centerY + offsetY;

            const point2X = Math.cos(sectorMiddle) * radius + centerX + offsetX;
            const point2Y = Math.sin(sectorMiddle) * radius + centerY + offsetY;

            const point3X = Math.cos(sectorMiddle) * radius + centerX + offsetX;
            const point3Y = Math.sin(sectorMiddle) * radius + centerY + offsetY;

            const point4X = Math.cos(sectorEnd) * radius + centerX + offsetX;
            const point4Y = Math.sin(sectorEnd) * radius + centerY + offsetY;

            return `<path class="pie-chart-drawing ${id} pie-chart-handle-hover" data-hover-group=".${id}" d="M ${point0X} ${point0Y} L ${point1X} ${point1Y} A ${radius} ${radius} 0 0 1 ${point2X} ${point2Y} Z" fill="${color}" stroke="${color}" stroke-width="0.5"/><path class="pie-chart-drawing ${id} pie-chart-handle-hover" data-hover-group=".${id}" d="M ${point0X} ${point0Y} L ${point3X} ${point3Y} A ${radius} ${radius} 0 0 1 ${point4X} ${point4Y} Z" fill="${color}" stroke="${color}" stroke-width="0.5"/>`;
        }
    }

    function generateShadow(id, sectorBegin, sectorEnd, color) {
        if (sectorEnd < 0) {
            return "";
        }

        if (sectorBegin > Math.PI) {
            return "";
        }

        const offsetDir = (sectorBegin + sectorEnd) / 2;

        if (sectorBegin < 0) {
            sectorBegin = 0;
        }

        if (sectorEnd > Math.PI) {
            sectorEnd = Math.PI;
        }

        const offsetX = Math.cos(offsetDir) * offset;
        const offsetY = Math.sin(offsetDir) * offset;

        const point1X = Math.cos(sectorEnd) * radius + centerX + offsetX;
        const point1Y = Math.sin(sectorEnd) * radius + centerY + offsetY;

        const point2X = Math.cos(sectorBegin) * radius + centerX + offsetX;
        const point2Y = Math.sin(sectorBegin) * radius + centerY + offsetY;

        const point3X = point2X;
        const point3Y = point2Y + depth;

        const point4X = point1X;
        const point4Y = point1Y + depth;

        const shadowColor = tinycolor(color).darken(20).saturate(-15).toHexString();

        return `<path class="pie-chart-drawing ${id} pie-chart-handle-hover" data-hover-group=".${id}" d="M ${point1X} ${point1Y} A ${radius} ${radius} 0 0 0 ${point2X} ${point2Y} L ${point3X} ${point3Y} A ${radius} ${radius} 0 0 1 ${point4X} ${point4Y} Z" fill="${shadowColor}"/>`;

    }

    function generateSide1(id, sectorBegin, sectorEnd, color) {
        if (sectorBegin < Math.PI / 2) {
            return "";
        }

        const offsetDir = (sectorBegin + sectorEnd) / 2;
        const offsetX = Math.cos(offsetDir) * offset;
        const offsetY = Math.sin(offsetDir) * offset;

        const point0X = centerX + offsetX;
        const point0Y = centerY + offsetY;
        
        const point1X = point0X;
        const point1Y = point0Y + depth;

        const point3X = Math.cos(sectorBegin) * radius + centerX + offsetX;
        const point3Y = Math.sin(sectorBegin) * radius + centerY + offsetY;

        const point2X = point3X;
        const point2Y = point3Y + depth;

        const sideColor = tinycolor(color).darken(30).saturate(-15).toHexString();

        return `<path class="pie-chart-drawing ${id} pie-chart-handle-hover" data-hover-group=".${id}" d="M ${point0X} ${point0Y} L ${point1X} ${point1Y} L ${point2X} ${point2Y} L ${point3X} ${point3Y} Z" fill="${sideColor}"/>`;
    }

    function generateSide2(id, sectorBegin, sectorEnd, color) {
        if (sectorEnd > Math.PI / 2) {
            return "";
        }

        const offsetDir = (sectorBegin + sectorEnd) / 2;
        const offsetX = Math.cos(offsetDir) * offset;
        const offsetY = Math.sin(offsetDir) * offset;

        const point0X = centerX + offsetX;
        const point0Y = centerY + offsetY;
        
        const point1X = point0X;
        const point1Y = point0Y + depth;

        const point3X = Math.cos(sectorEnd) * radius + centerX + offsetX;
        const point3Y = Math.sin(sectorEnd) * radius + centerY + offsetY;

        const point2X = point3X;
        const point2Y = point3Y + depth;

        const sideColor = tinycolor(color).darken(30).saturate(-15).toHexString();

        return `<path class="pie-chart-drawing ${id} pie-chart-handle-hover" data-hover-group=".${id}" d="M ${point0X} ${point0Y} L ${point1X} ${point1Y} L ${point2X} ${point2Y} L ${point3X} ${point3Y} Z" fill="${sideColor}"/>`;
    }
}

// @widgetLoadingMode:lazy-once